import { ReactElement, useState } from 'react';
import { platformCustomViewEditPermission } from '@agilelab/plugin-wb-rbac-common';
import { usePublishEvent } from '@agilelab/plugin-wb-events';
import { descriptorDownloadedTopic } from '@agilelab/plugin-wb-events-common';
import { usePermission } from '@backstage/plugin-permission-react';
import { ListItemIcon, MenuItem, MenuList, Paper } from '@material-ui/core';
import EditIcon from '@material-ui/icons/Edit';
import GetApp from '@material-ui/icons/GetApp';
import { Link } from 'react-router-dom';
import YAML from 'yaml';
import { saveYaml } from '../../../utils';
import { WbMoreOptions } from '../../WbMoreOptions';
import { CustomViewSubMenu } from '../CustomViewSubMenu';
import { CustomViewPages } from '../pages';
import { extractIncludeFromCode } from '../utils';

type CustomViewSummaryMenuProps = {
  customViewIds: string[];
  withMenu?: boolean;
  getUrlByid: (id: string) => string;
  showDownloadDescriptorButton?: boolean;
  dataProductDescriptor?: { id?: string; [key: string]: any };
  additionalData?:
    | { environment?: string }
    | { parentSystem: { urn: string; environment: string } };
};

export const CustomViewSummaryMenu = ({
  customViewIds,
  withMenu,
  getUrlByid,
  showDownloadDescriptorButton = false,
  dataProductDescriptor,
  additionalData,
}: CustomViewSummaryMenuProps) => {
  const { publish } = usePublishEvent({ generatorSystem: 'marketplace' });
  const { allowed: canEdit } = usePermission({
    permission: platformCustomViewEditPermission,
  });
  const [menuOpen, setMenuOpen] = useState(false);

  const onMenuClose = () => setMenuOpen(false);

  const customViews = customViewIds?.map(customViewId => {
    return CustomViewPages?.get(customViewId);
  });
  const included = customViews?.flatMap(child =>
    extractIncludeFromCode(child as ReactElement, '', ''),
  );
  const menu = customViewIds
    ?.map(id => {
      return {
        id: id,
        label: CustomViewPages.getMetadata(id)?.label ?? id,
      };
    })
    .concat(
      included?.map((inc: any) => ({
        id: inc.id,
        label: CustomViewPages.getMetadata(inc.id)?.label ?? inc.id,
      })),
    );

  if (!canEdit) return <></>;
  const subMenu = (
    <CustomViewSubMenu text="Custom Views" icon={<EditIcon fontSize="small" />}>
      <MenuList
        style={{
          overflow: 'auto',
          maxHeight: 'calc(100vh - 16px)',
          padding: 0,
        }}
      >
        <Paper style={{ borderRadius: 0, background: 'white' }}>
          {menu?.map(el => (
            <MenuItem key={el.id} component={Link} to={getUrlByid(el.id)}>
              {el.label}
            </MenuItem>
          ))}
        </Paper>
      </MenuList>
    </CustomViewSubMenu>
  );
  if (!withMenu) return subMenu;

  const downloadDescriptorButton = (
    <MenuItem
      onClick={() => {
        saveYaml(
          dataProductDescriptor?.id || 'descriptor',
          YAML.stringify(dataProductDescriptor || {}),
        );

        if (dataProductDescriptor?.id)
          publish(descriptorDownloadedTopic, {
            entityUrn: dataProductDescriptor?.id,
            kind: dataProductDescriptor?.kind,
            ...additionalData,
          });
      }}
    >
      <ListItemIcon>
        <GetApp />
      </ListItemIcon>
      Download Descriptor
    </MenuItem>
  );

  return (
    <WbMoreOptions
      MenuProps={{ MenuListProps: { style: { paddingBlock: '4px' } } }}
      onClick={e => {
        e.stopPropagation();
        setMenuOpen(true);
      }}
      open={menuOpen}
      onClose={onMenuClose}
    >
      {subMenu}
      {showDownloadDescriptorButton && downloadDescriptorButton}
    </WbMoreOptions>
  );
};
